import { FC } from 'react';
import { Box, Button, Flex, Icon, SimpleGrid, Text } from '@chakra-ui/react';
import {
  LuArrowDown,
  LuArrowLeft,
  LuArrowRight,
  LuArrowUp,
  LuEye,
  LuEyeOff,
  LuMaximize2,
  LuMinimize2,
} from 'react-icons/lu';
import type { IconType } from 'react-icons';
import theme from '@adflow/theme';
import {
  listAnimationPresets,
  type AnimationPresetGroup,
  type AnimationPresetId,
  type AnimationPresetPhase,
} from '../../../animation';

const { mcBlue, mcGreen, mcOrange } = theme.palette;
const selectedBg = theme.palette.secondary.main;
/** Pink is in the Animate section only  — not a Marketing Cloud token. */
const SLIDE_HORIZONTAL_PINK = '#E85A9B';

const GROUP_ORDER: AnimationPresetGroup[] = ['fade', 'slide', 'scale'];

const GROUP_LABELS: Record<AnimationPresetGroup, string> = {
  fade: 'Fade',
  slide: 'Slide',
  scale: 'Scale',
};

const ICON_COLORS: Record<AnimationPresetId, string> = {
  'fade-in': mcOrange.main,
  'fade-out': mcOrange.main,
  'grow-in': mcBlue.main,
  'shrink-out': mcBlue.main,
  'slide-in-left': SLIDE_HORIZONTAL_PINK,
  'slide-in-right': SLIDE_HORIZONTAL_PINK,
  'slide-in-up': mcGreen.main,
  'slide-in-down': mcGreen.main,
  'slide-out-left': SLIDE_HORIZONTAL_PINK,
  'slide-out-right': SLIDE_HORIZONTAL_PINK,
  'slide-out-up': mcGreen.main,
  'slide-out-down': mcGreen.main,
};

const PRESET_ICONS: Record<AnimationPresetId, IconType> = {
  'fade-in': LuEye,
  'fade-out': LuEyeOff,
  'grow-in': LuMaximize2,
  'shrink-out': LuMinimize2,
  'slide-in-left': LuArrowRight,
  'slide-in-right': LuArrowLeft,
  'slide-in-up': LuArrowDown,
  'slide-in-down': LuArrowUp,
  'slide-out-left': LuArrowLeft,
  'slide-out-right': LuArrowRight,
  'slide-out-up': LuArrowUp,
  'slide-out-down': LuArrowDown,
};

const groupHeadingStyles = {
  fontSize: 'xs',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'gray.500',
};

type Props = {
  phase: AnimationPresetPhase;
  appliedPresetIds: ReadonlySet<string>;
  onApply: (presetId: string) => void;
};

export const PresetPicker: FC<Props> = ({ phase, appliedPresetIds, onApply }) => {
  const presets = listAnimationPresets(phase);
  const groups = GROUP_ORDER.map((group) => ({
    group,
    label: GROUP_LABELS[group],
    presets: presets.filter((preset) => preset.group === group),
  })).filter((entry) => entry.presets.length > 0);

  return (
    <Flex direction="column" gap={4}>
      {groups.map(({ group, label, presets: groupPresets }) => (
        <Flex key={group} direction="column" gap={2}>
          <Text {...groupHeadingStyles}>{label}</Text>
          <SimpleGrid columns={2} spacing={2}>
            {groupPresets.map((preset) => {
              const isApplied = appliedPresetIds.has(preset.id);
              return (
                <Button
                  key={preset.id}
                  variant="outline"
                  w="100%"
                  h="72px"
                  minH="72px"
                  py={2}
                  px={2}
                  flexDirection="column"
                  gap={1}
                  whiteSpace="normal"
                  borderRadius="md"
                  borderColor={isApplied ? mcBlue.main : 'gray.200'}
                  bg={isApplied ? selectedBg : 'white'}
                  color={mcBlue.main}
                  fontWeight="semibold"
                  _hover={{
                    borderColor: mcBlue.main,
                    bg: isApplied ? selectedBg : 'gray.50',
                  }}
                  onClick={() => onApply(preset.id)}
                  data-test-id={`preset-card-${preset.id}`}
                  aria-pressed={isApplied}
                >
                  <Box
                    bg={ICON_COLORS[preset.id]}
                    color="white"
                    w="20px"
                    h="20px"
                    borderRadius="sm"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon as={PRESET_ICONS[preset.id]} boxSize={3} />
                  </Box>
                  <Text fontSize="xs" fontWeight="semibold" color={mcBlue.main} lineHeight="short">
                    {preset.label}
                  </Text>
                </Button>
              );
            })}
          </SimpleGrid>
        </Flex>
      ))}
    </Flex>
  );
};
